import React from 'react';
import { ArrowRight } from 'react-feather';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { BenefitsList } from '../BenefitsList';
import { DemoButton } from '../DemoButton';
import { Headline } from '../Headline';
import { NewBadge } from '../NewBadge';

const SectionWrapper = styled('section')`
  padding: 100px 24px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 960px) {
    padding: 80px 24px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 60px;

  @media (max-width: 960px) {
    margin-bottom: 40px;
  }
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin-top: 16px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SolutionCard = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: center;
  background-color: #fff;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 20px;
`;

const SolutionDescription = styled('p')`
  font-size: 16px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
`;

const PerfectFor = styled('div')`
  margin-top: 8px;
`;

const PerfectForLabel = styled('p')`
  font-size: 14px;
  font-weight: 600;
  color: #4f4f4f;
  margin: 0 0 12px 0;
`;

const TeamList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TeamItem = styled('li')`
  font-size: 14px;
  color: #4f4f4f;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${COLORS.PRIMARY};
    font-weight: bold;
  }
`;

const BenefitsListWrapper = styled('div')`
  margin-bottom: 60px;
`;

const MerchantSignupLink = styled('a')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #ff6b35;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
`;

const ButtonContainer = styled('div')`
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const stats = [
  {
    benefitValue: '13M+',
    explanation: 'Searchable links across 5M+ premium articles'
  },
  {
    benefitValue: '$3B+',
    explanation: 'GMV delivered annually for brands by publishers'
  },
  {
    benefitValue: '5min',
    explanation: 'Time it takes to set up a brand or agency account'
  }
];

export const MerchantSolutionsSection = () => {
  return (
    <SectionWrapper id="merchant-solutions">
      <SectionHeader>
        <Headline component="h2">Brand Solutions</Headline>
        <SectionDescription>
          Increase GMV, first-party data, and brand visibility by scaling
          high-intent commerce placements across premium publishers. AI-driven
          discovery and premium automation make it easy to find, fund, and scale
          thousands of high-intent placements at once.
        </SectionDescription>
      </SectionHeader>
      <BenefitsListWrapper>
        <BenefitsList benefits={stats} />
      </BenefitsListWrapper>
      <SolutionCard>
        <Headline
          component="h3"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
        >
          Amplify for Brands <NewBadge>New</NewBadge>
        </Headline>

        <img
          src="/images/amplify-for-merchants.png"
          alt="Amplify for Brands"
          style={{ width: '90%', height: 'auto', margin: '0 auto' }}
        />
        <SolutionDescription>
          Increase GMV, first-party data, and brand visibility by scaling
          high-intent commerce placements across premium publishers. AI-driven
          discovery and premium automation make it easy to find, fund, and scale
          thousands of high-intent placements at once.
        </SolutionDescription>
        <ContentWrapper>
          <PerfectFor>
            <PerfectForLabel>
              Perfect for brands and agencies who want:
            </PerfectForLabel>
            <TeamList>
              <TeamItem>Measurable GMV growth</TeamItem>
              <TeamItem>Access to proven, high-intent demand at scale</TeamItem>
              <TeamItem>
                To capture first-party data by becoming the linked seller
              </TeamItem>
              <TeamItem>
                To maintain visibility in content that shapes AI and LLM results
              </TeamItem>
              <TeamItem>
                Market intelligence to win and maintain ownable links
              </TeamItem>
              <TeamItem>
                Brand presence where credibility and influence are built
              </TeamItem>
            </TeamList>
          </PerfectFor>
          <ButtonContainer>
            <DemoButton
              text="Get a demo"
              variant="contained"
              style={{ width: 'fit-content' }}
            />
            <MerchantSignupLink
              href="https://demand.affilimate.com/signup"
              target="_blank"
            >
              Create a free account <ArrowRight size={18} />
            </MerchantSignupLink>
          </ButtonContainer>
        </ContentWrapper>
      </SolutionCard>
    </SectionWrapper>
  );
};
